import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import Loading from '../components/Loading';
import { Employee, CreateEmployeeDto, CreateEmployeeResponseDto } from '../types';
import './Pages.css';

const Employees: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<Omit<CreateEmployeeDto, 'phone'>>({
    name: '',
    email: ''
  });
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await api.get<Employee[]>('/admin/employees');
      setEmployees(response.data);
    } catch (error: any) {
      console.error('Error loading employees:', error);
      setError(error.response?.data?.message || 'Erro ao carregar funcionários');
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (value: string) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Formata: (XX) XXXXX-XXXX
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
    setError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!phone.trim()) {
      setError('Telefone é obrigatório');
      return false;
    }
    // Validar se tem pelo menos 10 dígitos
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setError('Telefone deve ter pelo menos 10 dígitos');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      // Remove formatação do telefone (mantém apenas números)
      const cleanPhone = phone.replace(/\D/g, '');
      const phoneWithCountryCode = `+55${cleanPhone}`;

      const response = await api.post<CreateEmployeeResponseDto>('/admin/employees', {
        ...formData,
        phone: phoneWithCountryCode
      });
      
      setSuccessMessage(response.data.message || 'Funcionário cadastrado com sucesso!');
      
      // Limpar formulário
      setFormData({
        name: '',
        email: ''
      });
      setPhone('');

      // Recarregar lista
      await loadEmployees();

      // Fechar modal após 2 segundos
      setTimeout(() => {
        setShowAddModal(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error: any) {
      console.error('Error creating employee:', error);
      setError(error.response?.data?.message || 'Erro ao cadastrar funcionário. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setFormData({
      name: '',
      email: ''
    });
    setPhone('');
    setError('');
    setSuccessMessage('');
  };

  if (loading) return <Loading message="Carregando funcionários..." />;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Funcionários</h1>
          <p>Gerencie os funcionários do seu restaurante</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          ➕ Adicionar Novo Funcionário
        </button>
      </div>

      <div className="clients-table">
        <div className="table-header">
          <div>Funcionário</div>
          <div>Telefone</div>
          <div>Email</div>
          <div>Cadastrado em</div>
          <div>Status</div>
        </div>
        {employees.map((employee) => (
          <div 
            key={employee.employeeRestaurantId} 
            className="table-row"
          >
            <div className="client-info">
              <div className="client-avatar">
                {employee.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="client-name">{employee.name}</div>
                <div className="client-since">
                  ID: {employee.employeeRestaurantId.substring(0, 8)}
                </div>
              </div>
            </div>
            <div>{employee.phone}</div>
            <div>{employee.email || 'N/A'}</div>
            <div>{new Date(employee.createdAt).toLocaleDateString('pt-BR')}</div>
            <div>
              <span className={`status-badge ${employee.isActive ? 'active' : 'inactive'}`}>
                {employee.isActive ? 'Ativo' : 'Inativo'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {employees.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">👷</div>
          <h3>Nenhum funcionário cadastrado</h3>
          <p>Adicione funcionários para que eles possam acessar o sistema</p>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
              <h2>Adicionar Novo Funcionário</h2>
              <button
                onClick={handleCloseModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {error && (
                <div style={{
                  padding: '12px 16px',
                  background: '#FEE2E2',
                  border: '1px solid #FCA5A5',
                  borderRadius: '8px',
                  color: '#DC2626',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}

              {successMessage && (
                <div style={{
                  padding: '12px 16px',
                  background: '#D1FAE5',
                  border: '1px solid #86EFAC',
                  borderRadius: '8px',
                  color: '#059669',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  {successMessage}
                </div>
              )}

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: 'var(--text-dark)',
                  fontSize: '14px'
                }}>
                  Nome *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Nome completo do funcionário"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '15px',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: 'var(--text-dark)',
                  fontSize: '14px'
                }}>
                  Telefone *
                </label>
                <div 
                  className="phone-input-group-modal"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => {
                    if (e.currentTarget.contains(document.activeElement)) {
                      e.currentTarget.style.borderColor = 'var(--primary)';
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                  }}
                >
                  <span style={{
                    padding: '12px 16px',
                    background: '#f5f5f5',
                    color: 'var(--text-dark)',
                    fontWeight: 600,
                    fontSize: '15px',
                    borderRight: '2px solid var(--border)',
                    whiteSpace: 'nowrap'
                  }}>
                    +55
                  </span>
                  <input
                    type="tel"
                    name="phone"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="(11) 98765-4321"
                    required
                    maxLength={15}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: 'none',
                      fontSize: '15px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      const container = e.currentTarget.parentElement;
                      if (container) {
                        container.style.borderColor = 'var(--primary)';
                      }
                    }}
                    onBlur={(e) => {
                      const container = e.currentTarget.parentElement;
                      if (container) {
                        container.style.borderColor = 'var(--border)';
                      }
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: 'var(--text-dark)',
                  fontSize: '14px'
                }}>
                  Email (opcional)
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="email@exemplo.com"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '15px',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                />
              </div>

              <div className="modal-actions">
                <button 
                  type="button"
                  className="btn-secondary" 
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Cadastrando...' : 'Cadastrar Funcionário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;

